import {
  createTheme,
  CssBaseline,
  Divider,
  FormControl,
  FormLabel,
  PaletteMode,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  changePasswordMutationFn,
  codeLoginMutationFn,
  codeLoginOnError,
  confirmMutationFn,
  confirmOnError,
  confirmOnSuccess,
  phoneOnError,
  phoneOnSuccess,
  verifyMutationFn,
} from "../../api/sign";
import { OdnIcon } from "../../components/Icon/CustomIcons";
import { PasswordTextField } from "../../components/PasswordTextField";
import { ErrorData } from "../../config/interfaces";
import { Card, SignUpContainer } from "../../config/styles";
import TemplateFrame from "../../utils/common/TemplateFrame";
import getSignTheme from "../../utils/common/theme/getSignTheme";
import { inputValidate } from "../../utils/inputValidate";
import { intlTelInputFunc } from "../../utils/intl-tel-input";
import { Timer } from "../../utils/timer";

const sx = {
  width: "100%",
  marginTop: 2,
  marginBottom: 1,
};

export default function ForgotPassword() {
  const [mode, setMode] = React.useState<PaletteMode>("light");
  const [showCustomTheme, setShowCustomTheme] = React.useState(true);
  const [showPassword, setShowPassword] = React.useState(false);
  const [expireTime, setExpireTime] = React.useState("");
  const [timerOn, setTimerOn] = React.useState(false);
  const [signUpReady, setSignUpReady] = React.useState(false);
  const [dialCode, setDialCode] = React.useState("");
  const [signinMethod, setSigninMethod] = React.useState("email");
  const defaultTheme = createTheme({ palette: { mode } });
  const SignTheme = createTheme(getSignTheme(mode));

  const [verifyCheck, setVerifyCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [codeCheck, setCodeCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [passwordCheck, setPasswordCheck] = React.useState<ErrorData>({
    error: false,
    message: "",
  });

  const [passwordConfirmCheck, setPasswordConfirmCheck] =
    React.useState<ErrorData>({
      error: false,
      message: "",
    });

  const navigate = useNavigate();

  React.useEffect(() => {
    const input = document.querySelector("#phone") as Element;
    intlTelInputFunc({ setDialCode: setDialCode, input: input });
  }, []);

  // This code only runs on the client side, to determine the system color preference
  React.useEffect(() => {
    // Check if there is a preferred mode in localStorage
    const savedMode = localStorage.getItem("themeMode") as PaletteMode | null;
    if (savedMode) {
      setMode(savedMode);
    } else {
      // If no preference is found, it uses system preference
      const systemPrefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      setMode(systemPrefersDark ? "dark" : "light");
    }
  }, []);

  const toggleColorMode = () => {
    const newMode = mode === "dark" ? "light" : "dark";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode); // Save the selected mode to localStorage
  };

  const toggleCustomTheme = () => {
    setShowCustomTheme((prev) => !prev);
  };

  const verifyMutation = useMutation({
    mutationFn: (data: Object) => verifyMutationFn(data),
    onError: () => phoneOnError(setVerifyCheck, dialCode),
    onSuccess: (data: AxiosResponse<any, any>) =>
      phoneOnSuccess({
        data: data,
        setCheck: setVerifyCheck,
        setTimerOn: setTimerOn,
        setState: setExpireTime,
        dialCode: dialCode,
      }),
  });

  const confirmMutation = useMutation({
    mutationFn: (data: Object) => confirmMutationFn(data),
    onError: () => confirmOnError(setCodeCheck, dialCode),
    onSuccess: (data: AxiosResponse<any, any>) =>
      confirmOnSuccess({
        data: data,
        setCheck: setCodeCheck,
        setTimerOn: setTimerOn,
        setState: setSignUpReady,
        dialCode: dialCode,
      }),
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: any) => changePasswordMutationFn(data),
    onError: (error) => console.log(error),
    // onSuccess: () => changePasswordOnSuccess(),
    onSuccess: () => navigate(-1),
  });

  const codeLoginMutation = useMutation({
    mutationFn: (data: Object) => codeLoginMutationFn(data),
    onError: (error) =>
      codeLoginOnError(error, setPasswordConfirmCheck, dialCode),
    onSuccess: async (data: AxiosResponse<any, any>) => {
      const password1 = document.getElementById(
        "change-password"
      ) as HTMLInputElement;
      const password2 = document.getElementById(
        "change-password-confirm"
      ) as HTMLInputElement;

      changePasswordMutation.mutate({
        password1: password1.value,
        password2: password2.value,
        headers: {
          Authorization: `${data?.data.type} ${data?.data.access_token}`,
        },
      });
    },
  });

  const validateInputs = () => {
    const inputData = [
      {
        type: "change-password",
        setState: setPasswordCheck,
      },
      {
        type: "change-password-confirm",
        setState: setPasswordConfirmCheck,
        mutation: codeLoginMutation as UseMutationResult,
        props: { dialCode: dialCode },
      },
    ];

    inputData.map((res) =>
      inputValidate({
        ...res,
        props: { dialCode: dialCode },
      })
    );
  };

  return (
    <TemplateFrame
      toggleCustomTheme={toggleCustomTheme}
      showCustomTheme={showCustomTheme}
      mode={mode}
      toggleColorMode={toggleColorMode}>
      <ThemeProvider theme={showCustomTheme ? SignTheme : defaultTheme}>
        <CssBaseline enableColorScheme />
        <SignUpContainer direction="column" justifyContent="space-between">
          <Card variant="outlined" style={{ overflowY: "auto" }}>
            <OdnIcon mode={mode} />

            <FormControl sx={{ ...sx }}>
              <FormLabel htmlFor={signinMethod}>
                <Link
                  onClick={() => setSigninMethod("email")}
                  sx={{
                    cursor: "pointer",
                    fontWeight: signinMethod === "email" ? "bold" : "lighter",
                    color: signinMethod === "email" ? "" : "gray",
                  }}>
                  {dialCode === "82" ? "이메일" : "Email"}
                </Link>

                <span style={{ fontWeight: "bolder" }}>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                </span>

                <Link
                  onClick={() => setSigninMethod("phone")}
                  sx={{
                    cursor: "pointer",
                    fontWeight: signinMethod === "phone" ? "bold" : "lighter",
                    color: signinMethod === "phone" ? "" : "gray",
                  }}>
                  {dialCode === "82" ? "전화번호" : "Phone"}
                </Link>
              </FormLabel>

              <TextField
                id="email"
                type="email"
                name="email"
                placeholder="example@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                error={verifyCheck.error}
                helperText={verifyCheck.message}
                color={verifyCheck.error ? "error" : "primary"}
                sx={{
                  ariaLabel: "email",
                  display: signinMethod === "email" ? "" : "none",
                }}
              />

              <TextField
                required
                autoFocus
                fullWidth
                name="phone"
                type="tel"
                id="phone"
                // placeholder="01000000000"
                autoComplete="tel"
                variant="outlined"
                error={verifyCheck.error}
                helperText={verifyCheck.message}
                color={verifyCheck.error ? "error" : "primary"}
                sx={{
                  ariaLabel: "tel",
                  display: signinMethod === "phone" ? "" : "none",
                }}
              />
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                inputValidate({
                  type: signinMethod,
                  setState: setVerifyCheck,
                  mutation: verifyMutation as UseMutationResult,
                  props: { dialCode: dialCode },
                });
              }}>
              {dialCode === "82" ? "인증코드 받기" : "Get verification code"}
            </Button>

            <FormControl sx={{ ...sx }}>
              <FormLabel htmlFor="code">
                {dialCode === "82" ? "인증코드" : "Verification code"}
              </FormLabel>
              <TextField
                required
                fullWidth
                name="code"
                placeholder="••••••"
                type="number"
                id="code"
                variant="outlined"
                error={codeCheck.error}
                helperText={codeCheck.message}
                color={codeCheck.error ? "error" : "primary"}
              />
            </FormControl>

            {timerOn ? (
              <Timer
                expireTime={expireTime}
                setTimerOn={setTimerOn}
                setState={setCodeCheck}
                signUpReady={signUpReady}
              />
            ) : null}

            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                inputValidate({
                  type: "code",
                  setState: setCodeCheck,
                  mutation: confirmMutation as UseMutationResult,
                  props: { dialCode: dialCode, method: signinMethod },
                })
              }>
              {dialCode === "82" ? "인증하기" : "Code verify"}
            </Button>

            {signUpReady ? (
              <>
                <FormControl sx={{ ...sx }}>
                  <FormLabel htmlFor="password">
                    {dialCode === "82" ? "새로운 비밀번호" : "New password"}
                  </FormLabel>
                  <PasswordTextField
                    passwordCheck={passwordCheck}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    type="change-password"
                  />
                </FormControl>
                <FormControl sx={{ ...sx }}>
                  <FormLabel htmlFor="password">
                    {dialCode === "82" ? "비밀번호 확인" : "Confirm password"}
                  </FormLabel>
                  <PasswordTextField
                    passwordCheck={passwordConfirmCheck}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    type="change-password-confirm"
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  onClick={validateInputs}>
                  Reset password
                </Button>
              </>
            ) : null}

            <Divider>or</Divider>

            <Typography sx={{ textAlign: "center" }}>
              <span>
                <Link
                  onClick={() => navigate(-1)}
                  variant="body2"
                  sx={{ alignSelf: "center", cursor: "pointer" }}>
                  {dialCode === "82" ? "로그인 화면으로" : "Back to log in"}
                </Link>
              </span>
            </Typography>
          </Card>
        </SignUpContainer>
      </ThemeProvider>
    </TemplateFrame>
  );
}
